import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiLoader, FiCheck, FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import {
  monremCheckStatus,
  monremRequestCallbackByOrder,
} from "../api/payment";
import { useSocket } from "../context/SocketContext";

const SOCKET_MONREM_PAYMENT = "monrem_payment";

const WAITING_STATUSES = new Set([
  "PENDING",
  "HELD",
  "AUTHORIZATION",
  "SETTLEMENT",
]);

function isApproved(status) {
  return status === "APPROVED";
}

function isWaiting(status) {
  return WAITING_STATUSES.has(status);
}

/**
 * MonRem return_url: `/payment/callback?order_id=...`
 * Legacy query: `status=success|error`, optional `message`.
 */
const PaymentCallbackPage = ({ isDark, setToast }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { socket } = useSocket();

  const setToastRef = useRef(setToast);
  setToastRef.current = setToast;

  const [uiPhase, setUiPhase] = useState("loading"); // loading | processing | success | error
  const [message, setMessage] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [lastStatus, setLastStatus] = useState(null);
  const [retryBusy, setRetryBusy] = useState(false);

  const pollTimerRef = useRef(null);
  const terminalRef = useRef(false);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";

  const applyRemoteStatus = useCallback((status, detail = {}) => {
    if (!status || terminalRef.current) return;

    setLastStatus(status);
    const desc =
      detail.status_description ||
      detail.message ||
      (isApproved(status) ? "Payment completed successfully." : null);

    if (isApproved(status)) {
      terminalRef.current = true;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      setUiPhase("success");
      setMessage(
        desc ||
          `Payment of ₾${Number(detail.amount || 0).toFixed(2)} completed successfully!`
      );
      window.dispatchEvent(new CustomEvent("balance-updated"));
      setToastRef.current?.("Payment successful!");
      return;
    }

    if (isWaiting(status)) {
      setUiPhase("processing");
      setMessage(
        desc ||
          "Payment is still processing. This page will update automatically."
      );
      return;
    }

    terminalRef.current = true;
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setUiPhase("error");
    setMessage(
      desc || `Payment status: ${status}. Please try again or contact support.`
    );
    setToastRef.current?.(desc || "Payment was not completed");
  }, []);

  const fetchStatusOnce = useCallback(async (oid) => {
    try {
      const result = await monremCheckStatus(oid);
      applyRemoteStatus(result.status, result);
    } catch (e) {
      terminalRef.current = true;
      setUiPhase("error");
      setMessage(
        e.response?.data?.message ||
          "Failed to verify payment. Check your connection and try again."
      );
      setToastRef.current?.("Payment verification failed");
    }
  }, [applyRemoteStatus]);

  useEffect(() => {
    const statusParam = searchParams.get("status");
    const messageParam = searchParams.get("message");
    const oid = searchParams.get("order_id");

    if (statusParam === "success") {
      terminalRef.current = true;
      setUiPhase("success");
      setMessage(messageParam || "Payment completed successfully!");
      window.dispatchEvent(new CustomEvent("balance-updated"));
      setToastRef.current?.("Payment successful!");
      return;
    }

    if (statusParam === "error") {
      terminalRef.current = true;
      setUiPhase("error");
      setMessage(messageParam || "Payment failed. Please try again.");
      setToastRef.current?.(messageParam || "Payment failed");
      return;
    }

    if (!oid) {
      terminalRef.current = true;
      setUiPhase("error");
      setMessage("Invalid callback: missing order reference.");
      return;
    }

    setOrderId(oid);
    terminalRef.current = false;
    setUiPhase("loading");
    setMessage("Verifying payment…");

    fetchStatusOnce(oid);

    pollTimerRef.current = setInterval(() => {
      if (terminalRef.current) return;
      fetchStatusOnce(oid);
    }, 4000);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [searchParams, fetchStatusOnce]);

  useEffect(() => {
    if (!socket || !orderId || terminalRef.current) return;

    const onMonremPayment = (payload) => {
      if (!payload || payload.source !== "monrem") return;
      if (payload.order_id !== orderId) return;
      applyRemoteStatus(payload.status, payload);
    };

    socket.on(SOCKET_MONREM_PAYMENT, onMonremPayment);
    return () => {
      socket.off(SOCKET_MONREM_PAYMENT, onMonremPayment);
    };
  }, [socket, orderId, applyRemoteStatus]);

  const handleRequestCallbackRetry = async () => {
    if (!orderId || retryBusy) return;
    setRetryBusy(true);
    try {
      await monremRequestCallbackByOrder(orderId);
      setToastRef.current?.(
        "Callback retry requested — updates may take a moment."
      );
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.response?.data?.error ||
        "Could not request callback retry.";
      setToastRef.current?.(msg);
    } finally {
      setRetryBusy(false);
    }
  };

  if (uiPhase === "loading") {
    return (
      <div className="flex w-full flex-col gap-space-24 font-ds">
        <div className="rounded-radius-lg bg-bg-raised p-space-24">
          <div className="flex flex-col items-center justify-center py-12">
            <FiLoader className="h-12 w-12 text-accent-text animate-spin mb-4" />
            <h2 className={`text-title-2 ${baseText}`}>
              Verifying payment…
            </h2>
            <p className={`text-subheadline ${mutedText} mt-2 text-center max-w-md`}>
              {message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (uiPhase === "processing") {
    return (
      <div className="flex w-full flex-col gap-space-24 font-ds">
        <div className="rounded-radius-lg bg-bg-raised p-space-24">
          <div className="flex flex-col items-center justify-center py-12">
            <FiLoader className="h-12 w-12 text-accent-text animate-spin mb-4" />
            <h2 className={`text-title-2 ${baseText}`}>
              Processing payment
            </h2>
            <p className={`text-subheadline ${mutedText} mt-2 text-center max-w-md`}>
              {message}
            </p>
            {lastStatus && (
              <p className={`text-footnote ${mutedText} mt-4 uppercase tracking-wider`}>
                Status: {lastStatus}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button
                type="button"
                onClick={() => orderId && fetchStatusOnce(orderId)}
                className="inline-flex h-button-m items-center rounded-radius-md bg-fill-secondary px-space-16 text-caps-button-m uppercase text-label-primary"
              >
                Check now
              </button>
              <button
                type="button"
                disabled={retryBusy}
                onClick={handleRequestCallbackRetry}
                className="px-5 py-2.5 rounded-radius-sm bg-accent-default text-accent-on text-subheadline-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {retryBusy ? (
                  <FiLoader className="h-4 w-4 animate-spin" />
                ) : (
                  <FiRefreshCw className="h-4 w-4" />
                )}
                Re-send server callback
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (uiPhase === "success") {
    return (
      <div className="flex w-full flex-col gap-space-24 font-ds">
        <div className="rounded-radius-lg bg-bg-raised p-space-24">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="size-16 rounded-full bg-accent-default flex items-center justify-center mb-6">
              <FiCheck className="size-8 text-accent-on" />
            </div>
            <h2 className={`text-title-1 ${baseText} mb-2`}>
              Payment Successful!
            </h2>
            <p className={`text-subheadline ${mutedText} text-center mb-6 max-w-md`}>
              {message}
            </p>
            <button
              onClick={() => navigate("/")}
              className="inline-flex h-button-m items-center rounded-radius-md bg-accent-default px-space-16 text-caps-button-m uppercase text-accent-on"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={`rounded-radius-lg border p-8 backdrop-blur ${cardBase}`}>
        <div className="flex flex-col items-center justify-center py-12">
          <div className="size-16 rounded-full bg-status-danger-bg flex items-center justify-center mb-6">
            <FiAlertCircle className="size-8 text-status-danger" />
          </div>
          <h2 className={`text-title-1 ${baseText} mb-2`}>
            Payment Failed
          </h2>
          <p className={`text-subheadline ${mutedText} text-center mb-6 max-w-md`}>
            {message}
          </p>
          <div className="flex gap-4 flex-wrap justify-center">
            {orderId && (
              <button
                type="button"
                disabled={retryBusy}
                onClick={handleRequestCallbackRetry}
                className="px-6 py-3 rounded-radius-sm border font-semibold transition disabled:opacity-50 flex items-center gap-2"
              >
                {retryBusy ? (
                  <FiLoader className="h-4 w-4 animate-spin" />
                ) : (
                  <FiRefreshCw className="h-4 w-4" />
                )}
                Retry callback
              </button>
            )}
            <button
              onClick={() => navigate("/payment")}
              className="inline-flex h-button-m items-center rounded-radius-md bg-accent-default px-space-16 text-caps-button-m uppercase text-accent-on"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/")}
              className={`px-6 py-3 rounded-radius-sm border font-semibold transition ${
                "border-separator-default text-label-primary hover:border-separator-opaque"
              }`}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
